/**
 * ClientMessagingPro — Refonte UX 07/05/2026 (inspirée maquette client moderne)
 * - 3 colonnes : Liste des conversations · Thread · Détails de la conversation
 * - Bulles vertes pâles (own) + blanches avec ombre légère
 * - Header chat avec actions (recherche, appel, more)
 * - Tabs (Tous / Non lus / Ouverts) + recherche + filtre
 * - Panneau de détails avec statut, priorité, numéro de ticket, opération liée, etc.
 * Conserve : WebSocket, typing, file upload, sound, lastSeenAt, notifications.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Search, Send, Paperclip, MoreVertical, Smile,
  Check, CheckCheck, Clock, FileText, Edit3,
  MessageSquare, Package, ArrowLeft, X, Phone,
  Truck, Headphones, Volume2, VolumeX, ChevronDown,
  Copy, ExternalLink, BookOpen, MapPin, SlidersHorizontal,
  AlertCircle, RotateCcw,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import useChatSocket from '../hooks/useChatSocket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Notification sound (silent base64 mp3 for graceful degradation)
const NOTIFICATION_SOUND = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYNBrv/AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//tQZAAP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//tQZB8P8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAEVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';

// ============================================================
// Helpers
// ============================================================
const initialsOf = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();
};

const getTimeAgo = (date) => {
  if (!date) return '';
  const now = new Date();
  const msgDate = new Date(date);
  const diffMs = now - msgDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'À l\'instant';
  if (diffMins < 60) return `${diffMins}min`;
  if (diffHours < 24) return msgDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Hier';
  if (diffDays < 7) return msgDate.toLocaleDateString('fr-FR', { weekday: 'short' });
  return msgDate.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

const getTimeString = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const getCategoryLabel = (type) => {
  switch (type) {
    case 'OPERATION': return "Suivi d'opération";
    case 'DIRECT': return 'Demande directe';
    case 'GENERAL': return 'Question générale';
    default: return 'Conversation';
  }
};

const getStatusBadge = (status) => {
  if (status === 'RESOLVED') return { label: 'Résolu', dot: 'bg-zinc-400', text: 'text-zinc-600' };
  return { label: 'Ouvert', dot: 'bg-emerald-500', text: 'text-emerald-700' };
};

// ============================================================
// NotificationAlert (popup)
// ============================================================
const NotificationAlert = ({ message, onDismiss }) => {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 5000);
    return () => clearTimeout(timer);
  }, [onDismiss]);
  return (
    <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 fade-in duration-300">
      <div className="bg-white rounded-xl shadow-2xl border border-zinc-100 p-4 max-w-sm flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center shrink-0">
          <Headphones className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-zinc-900 text-sm">Support Baobab</p>
          <p className="text-zinc-600 text-sm truncate">{message.content}</p>
        </div>
        <button onClick={onDismiss} className="text-zinc-400 hover:text-zinc-600">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================================
// ConversationItem — 3 états distincts : lu / non lu / sélectionné
// (combine couleur + graisse + barre + badge + dot + icône pour accessibilité)
// ============================================================
const ConversationItem = ({ conversation, isSelected, onClick }) => {
  const unread = conversation.unreadCount || conversation.unreadCountClient || 0;
  const hasUnread = unread > 0;

  // Combinaisons d'états
  // - Non lu (priorité visuelle max) : fond émeraude + barre verte + bold + badge + dot
  // - Sélectionné simple (lu)        : fond gris neutre + ring discret, pas de barre
  // - Sélectionné + non lu           : on garde les marqueurs non lus (priorité) + ring émeraude
  // - Lu non sélectionné             : fond blanc, hover gris, normal weight
  const containerCls = hasUnread
    ? `bg-emerald-50/70 border-l-[3px] border-l-emerald-500 hover:bg-emerald-50 ${isSelected ? 'ring-1 ring-inset ring-emerald-300' : ''}`
    : isSelected
      ? 'bg-zinc-100 border-l-[3px] border-l-zinc-300 hover:bg-zinc-100'
      : 'bg-white border-l-[3px] border-l-transparent hover:bg-zinc-50';

  return (
    <div
      onClick={onClick}
      role="button"
      aria-label={hasUnread ? `Conversation non lue : ${unread} message${unread > 1 ? 's' : ''} non lu${unread > 1 ? 's' : ''}` : 'Conversation lue'}
      className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-all duration-150 relative ${containerCls}`}
      data-testid={`conv-item-${conversation.id || conversation._id}`}
      data-unread={hasUnread ? 'true' : 'false'}
      data-selected={isSelected ? 'true' : 'false'}
    >
      {/* Pastille notification (signal additionnel non-couleur-only) */}
      {hasUnread && (
        <span className="absolute top-3.5 right-3 flex h-2 w-2" aria-hidden="true">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
      )}

      {/* Avatar */}
      <div className="relative shrink-0">
        <Avatar className="h-11 w-11 bg-emerald-600">
          <AvatarFallback className="bg-emerald-600 text-white">
            <Headphones className="w-5 h-5" />
          </AvatarFallback>
        </Avatar>
        <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full" aria-label="En ligne" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5 gap-2">
          <span className={`text-sm truncate ${hasUnread ? 'font-bold text-zinc-900' : 'font-medium text-zinc-700'}`}>
            Support Baobab
          </span>
          <span className={`text-[11px] shrink-0 tabular-nums ${
            hasUnread ? 'text-emerald-700 font-bold' : 'text-zinc-400 font-normal'
          }`}>
            {getTimeAgo(conversation.lastMessageAt)}
          </span>
        </div>

        {conversation.operationReference && (
          <div className="flex items-center gap-1 mb-0.5">
            <Package className={`w-3 h-3 shrink-0 ${hasUnread ? 'text-emerald-700' : 'text-zinc-400'}`} />
            <span className={`text-[11px] truncate ${hasUnread ? 'text-emerald-800 font-semibold' : 'text-zinc-500 font-normal'}`}>
              {conversation.operationReference}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pr-3">
          <p className={`text-xs truncate flex-1 leading-snug ${
            hasUnread ? 'text-zinc-800 font-semibold' : 'text-zinc-500 font-normal'
          }`}>
            {conversation.lastMessagePreview || <span className="italic text-zinc-400">Aucun message</span>}
          </p>
          {hasUnread && (
            <span
              className="shrink-0 min-w-[20px] h-5 flex items-center justify-center bg-emerald-600 text-white text-[10px] font-bold rounded-full px-1.5 shadow-sm"
              aria-label={`${unread} non lu${unread > 1 ? 's' : ''}`}
              data-testid={`unread-badge-${conversation.id || conversation._id}`}
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MessageBubble — refonte (bulles vertes pâles côté own)
// ============================================================
const ROLE_CONFIG = {
  admin: { label: 'Admin', avatar: 'bg-purple-500' },
  commercial: { label: 'Commercial', avatar: 'bg-emerald-600' },
  operateur: { label: 'Opérateur', avatar: 'bg-blue-500' },
  operator: { label: 'Opérateur', avatar: 'bg-blue-500' },
  field_ops: { label: 'Terrain', avatar: 'bg-amber-500' },
};

const MessageBubble = ({ message, isOwn, isLatestFromAgent, onRetry }) => {
  const renderAttachment = (attachment) => {
    const isImage = attachment.type?.startsWith('image/') ||
                    attachment.filename?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
    if (isImage) {
      return (
        <div className="mt-2 rounded-lg overflow-hidden max-w-xs">
          <img src={attachment.url || `${API}${attachment.path}`} alt={attachment.filename} className="w-full h-auto" />
        </div>
      );
    }
    return (
      <a href={attachment.url || `${API}${attachment.path}`} target="_blank" rel="noopener noreferrer"
        className={`mt-2 flex items-center gap-2 p-2 rounded-lg ${isOwn ? 'bg-emerald-200/50' : 'bg-zinc-100'}`}>
        <FileText className={`w-7 h-7 ${isOwn ? 'text-emerald-700' : 'text-zinc-500'}`} />
        <p className={`text-sm font-medium truncate ${isOwn ? 'text-emerald-900' : 'text-zinc-700'}`}>{attachment.filename}</p>
      </a>
    );
  };

  // Statut d'envoi : sending | sent | delivered | read | error
  // Backend expose `status` ou `readBy[]` ; on dérive si nécessaire
  const deriveStatus = () => {
    if (message.error || message.status === 'error') return 'error';
    if (message.sending || message.status === 'sending') return 'sending';
    if (message.status === 'read' || (message.readBy && message.readBy.length > 1)) return 'read';
    if (message.status === 'delivered') return 'delivered';
    return 'sent';
  };
  const status = deriveStatus();

  const StatusIcon = () => {
    switch (status) {
      case 'sending':
        return <Clock className="w-3.5 h-3.5 text-emerald-700/40" aria-label="Envoi en cours" />;
      case 'sent':
        return <Check className="w-3.5 h-3.5 text-emerald-700/50" aria-label="Envoyé" />;
      case 'delivered':
        return <CheckCheck className="w-3.5 h-3.5 text-emerald-700/60" aria-label="Livré" />;
      case 'read':
        return (
          <span className="flex items-center gap-0.5" aria-label="Lu">
            <CheckCheck className="w-3.5 h-3.5 text-emerald-600" />
          </span>
        );
      case 'error':
        return (
          <button
            onClick={(e) => { e.stopPropagation(); onRetry && onRetry(message); }}
            className="flex items-center gap-1 text-red-500 hover:text-red-600 text-[10px] font-semibold"
            data-testid={`retry-msg-${message.id || message._id || 'tmp'}`}
            aria-label="Erreur d'envoi, cliquer pour réessayer"
          >
            <AlertCircle className="w-3.5 h-3.5" />
            <RotateCcw className="w-3 h-3" />
            <span>Réessayer</span>
          </button>
        );
      default:
        return null;
    }
  };

  const roleKey = (message.senderType || '').toLowerCase();
  const role = ROLE_CONFIG[roleKey];
  const senderName = message.senderName || (isOwn ? 'Vous' : 'Support Baobab');
  const isAgentOnline = (() => {
    if (isOwn || !isLatestFromAgent) return false;
    try {
      const t = message.senderLastSeenAt;
      if (!t) return false;
      return (Date.now() - new Date(t).getTime()) < 5 * 60 * 1000;
    } catch { return false; }
  })();

  const bubbleErrorRing = status === 'error' ? 'ring-1 ring-red-300' : '';

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2.5`}>
      <div className={`max-w-[72%] flex items-end gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}>
        {!isOwn && (
          <div className="relative shrink-0">
            <Avatar className={`h-8 w-8 ${role?.avatar || 'bg-emerald-600'}`}>
              <AvatarFallback className={`text-white text-xs font-semibold ${role?.avatar || 'bg-emerald-600'}`}>
                {message.senderType ? initialsOf(senderName) : <Headphones className="w-4 h-4" />}
              </AvatarFallback>
            </Avatar>
            {isAgentOnline && (
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" title="En ligne" />
            )}
          </div>
        )}

        <div className={`relative px-3.5 py-2 rounded-2xl shadow-sm ${
          isOwn
            ? 'bg-emerald-100 text-emerald-950 rounded-br-md'
            : 'bg-white text-zinc-800 rounded-bl-md border border-zinc-100'
        } ${bubbleErrorRing}`}>
          {message.content && (
            <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{message.content}</p>
          )}

          {message.attachments?.map((att, idx) => (
            <div key={idx}>{renderAttachment(att)}</div>
          ))}

          <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
            <span className={`text-[10px] tabular-nums ${isOwn ? 'text-emerald-700/70' : 'text-zinc-400'}`}>
              {getTimeString(message.createdAt || message.timestamp)}
            </span>
            {isOwn && <StatusIcon />}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// ChatHeader — actions en haut
// ============================================================
const ChatHeader = ({ conversation, onBack, soundEnabled, onToggleSound, onToggleSearch, onToggleDetails }) => (
  <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-zinc-100 shadow-[0_1px_2px_rgb(0,0,0,0.03)]">
    <div className="flex items-center gap-3 min-w-0">
      <Button variant="ghost" size="icon" className="lg:hidden -ml-2" onClick={onBack}>
        <ArrowLeft className="h-5 w-5" />
      </Button>
      <div className="relative shrink-0">
        <Avatar className="h-10 w-10 bg-emerald-600">
          <AvatarFallback className="bg-emerald-600 text-white">
            <Headphones className="w-5 h-5" />
          </AvatarFallback>
        </Avatar>
        <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" />
      </div>
      <div className="min-w-0">
        <h3 className="font-semibold text-zinc-900 text-base truncate">Support Baobab</h3>
        <div className="flex items-center gap-1.5 text-xs">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          <span className="text-emerald-600 font-medium">En ligne</span>
        </div>
      </div>
    </div>

    <div className="flex items-center gap-1">
      <Button variant="ghost" size="icon" onClick={onToggleSearch} className="text-zinc-500 hover:text-emerald-700" title="Rechercher dans la conversation" data-testid="chat-header-search">
        <Search className="h-5 w-5" />
      </Button>
      <Button variant="ghost" size="icon" className="text-zinc-500 hover:text-emerald-700" title="Appel téléphonique (à venir)" data-testid="chat-header-phone">
        <Phone className="h-5 w-5" />
      </Button>
      <Button variant="ghost" size="icon" onClick={onToggleSound} className="text-zinc-500 hover:text-emerald-700" title={soundEnabled ? 'Désactiver le son' : 'Activer le son'} data-testid="chat-header-sound">
        {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
      </Button>
      <Button variant="ghost" size="icon" onClick={onToggleDetails} className="text-zinc-500 hover:text-emerald-700" title="Détails de la conversation" data-testid="chat-header-more">
        <MoreVertical className="h-5 w-5" />
      </Button>
    </div>
  </div>
);

// ============================================================
// MessageInput (composer) — refonte
// ============================================================
const MessageInput = ({ onSend, onTyping, typingUserName, disabled }) => {
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if ((!message.trim() && files.length === 0) || sending) return;
    setSending(true);
    try {
      await onSend(message.trim(), files);
      setMessage('');
      setFiles([]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...newFiles].slice(0, 5));
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  return (
    <div className="border-t border-zinc-100 bg-white px-5 pt-3 pb-3">
      {typingUserName && (
        <div className="flex items-center gap-2 px-2 pb-2 text-xs text-zinc-500 italic" data-testid="typing-indicator">
          <span className="flex gap-0.5">
            <span className="w-1.5 h-1.5 bg-emerald-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-1.5 h-1.5 bg-emerald-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-1.5 h-1.5 bg-emerald-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </span>
          <span>{typingUserName} est en train d'écrire…</span>
        </div>
      )}

      {files.length > 0 && (
        <div className="flex gap-2 mb-2 overflow-x-auto pb-1">
          {files.map((file, idx) => (
            <div key={idx} className="relative shrink-0 group">
              <div className="w-14 h-14 bg-zinc-100 rounded-lg flex items-center justify-center overflow-hidden border border-zinc-200">
                {file.type?.startsWith('image/') ? (
                  <img src={URL.createObjectURL(file)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <FileText className="w-5 h-5 text-zinc-400" />
                )}
              </div>
              <button onClick={() => removeFile(idx)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <Button type="button" variant="ghost" size="icon" onClick={() => fileInputRef.current?.click()}
          className="text-zinc-500 hover:text-emerald-700 shrink-0 h-10 w-10" data-testid="composer-attach">
          <Paperclip className="h-5 w-5" />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="text-zinc-500 hover:text-emerald-700 shrink-0 h-10 w-10" data-testid="composer-emoji" title="Emoji">
          <Smile className="h-5 w-5" />
        </Button>
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />

        <textarea
          value={message}
          onChange={(e) => { setMessage(e.target.value); onTyping && onTyping(); }}
          onKeyDown={handleKeyDown}
          placeholder="Écrivez votre message..."
          rows={1}
          disabled={disabled || sending}
          data-testid="message-input"
          className="flex-1 px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 text-sm transition-all"
          style={{ minHeight: '42px', maxHeight: '120px' }}
        />

        <Button
          type="submit"
          disabled={disabled || sending || (!message.trim() && files.length === 0)}
          className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-200 disabled:text-zinc-400 rounded-full h-10 px-5 gap-1.5 shadow-sm shrink-0 transition-all"
          data-testid="send-message-btn"
        >
          <Send className="h-4 w-4" />
          <span className="hidden sm:inline text-sm font-medium">Envoyer</span>
        </Button>
      </form>

      <p className="text-[11px] text-zinc-400 mt-2 px-2">
        Astuce : <kbd className="px-1.5 py-0.5 bg-zinc-100 rounded text-[10px]">Entrée</kbd> pour envoyer, <kbd className="px-1.5 py-0.5 bg-zinc-100 rounded text-[10px]">Shift</kbd> + <kbd className="px-1.5 py-0.5 bg-zinc-100 rounded text-[10px]">Entrée</kbd> pour un retour à la ligne.
      </p>
    </div>
  );
};

// ============================================================
// ConversationDetails (right panel)
// ============================================================
const ConversationDetails = ({ conversation, onClose }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  if (!conversation) return null;

  const status = getStatusBadge(conversation.status);
  const created = conversation.createdAt
    ? new Date(conversation.createdAt).toLocaleString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

  const copyTicket = async () => {
    const ref = conversation.incidentReference || `#INC-${(conversation.incidentId || '').slice(0, 6).toUpperCase()}`;
    try {
      await navigator.clipboard.writeText(ref);
      toast({ title: 'Copié', description: ref });
    } catch {}
  };

  return (
    <aside className="hidden xl:flex flex-col w-[300px] bg-zinc-50/50 border-l border-zinc-100 overflow-y-auto" data-testid="conversation-details-panel">
      {/* Header collapsible */}
      <div className="px-5 pt-5 pb-3">
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between text-sm font-semibold text-zinc-800"
          data-testid="toggle-details"
        >
          Détails de la conversation
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {open && (
        <div className="px-5 pb-5 space-y-5 text-sm">
          {/* Statut */}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold mb-1.5">Statut</p>
            <div className="flex items-center gap-2 px-3 py-2 bg-white border border-zinc-200 rounded-lg">
              <span className={`w-2 h-2 rounded-full ${status.dot}`} />
              <span className={`font-medium text-sm ${status.text}`}>{status.label}</span>
            </div>
          </div>

          {/* Numéro d'incident (UNIQUEMENT si un incident est lié à la discussion) */}
          {conversation.incidentId && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold mb-1.5">Incident lié</p>
              <div className="flex items-center justify-between gap-2 px-3 py-2 bg-white border border-zinc-200 rounded-lg">
                <span className="font-mono text-sm text-zinc-800">
                  {conversation.incidentReference || `#INC-${(conversation.incidentId || '').slice(0, 6).toUpperCase()}`}
                </span>
                <button onClick={copyTicket} className="text-zinc-400 hover:text-emerald-600 transition-colors" title="Copier" data-testid="copy-ticket-btn">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Créé le */}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold mb-1.5">Créé le</p>
            <p className="text-sm text-zinc-700">{created}</p>
          </div>

          {/* Catégorie */}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold mb-1.5">Catégorie</p>
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-zinc-700">{getCategoryLabel(conversation.type)}</span>
            </div>
          </div>

          {/* Opération liée */}
          {conversation.operationReference && (
            <div className="pt-2 border-t border-zinc-200">
              <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold mb-1.5">Opération liée</p>
              <button
                onClick={() => navigate(`/operations/${conversation.operationId}`)}
                className="flex items-center justify-between w-full text-emerald-700 hover:text-emerald-800 font-semibold text-sm group"
                data-testid="op-link"
              >
                {conversation.operationReference}
                <ExternalLink className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100" />
              </button>
              {conversation.operationDeparture && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-zinc-500">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>{conversation.operationDeparture}{conversation.operationArrival ? ` → ${conversation.operationArrival}` : ''}</span>
                </div>
              )}
              {conversation.operationStatus && (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-zinc-500">
                  <Truck className="w-3.5 h-3.5" />
                  <span className="capitalize">{(conversation.operationStatus || '').replace(/_/g, ' ')}</span>
                </div>
              )}
              <Button
                onClick={() => navigate(`/operations/${conversation.operationId}`)}
                variant="outline"
                className="w-full mt-3 h-9 text-sm gap-1.5"
                data-testid="view-op-btn"
              >
                Voir l'opération
                <ExternalLink className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}

          {/* Aide */}
          <div className="mt-4 p-4 bg-emerald-50/70 border border-emerald-100 rounded-xl">
            <div className="flex items-start gap-2 mb-2">
              <BookOpen className="w-4 h-4 text-emerald-700 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-emerald-900">Besoin d'aide ?</p>
                <p className="text-xs text-zinc-600 mt-1">Consultez notre centre d'aide pour des réponses rapides.</p>
              </div>
            </div>
            <Button variant="outline" className="w-full mt-2 h-8 text-xs gap-1.5 bg-white" data-testid="help-center-btn">
              Accéder au centre d'aide
              <ExternalLink className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
    </aside>
  );
};

// ============================================================
// WelcomeScreen (no conversations)
// ============================================================
const WelcomeScreen = ({ onStartConversation }) => (
  <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-emerald-50/50 to-white p-6">
    <div className="text-center max-w-md">
      <div className="w-24 h-24 mx-auto mb-6 bg-emerald-600 rounded-full flex items-center justify-center shadow-lg">
        <Headphones className="w-12 h-12 text-white" />
      </div>
      <h2 className="text-2xl font-bold text-zinc-800 mb-3">Bienvenue sur la messagerie Baobab</h2>
      <p className="text-zinc-600 mb-6 leading-relaxed">
        Notre équipe est disponible pour répondre à toutes vos questions concernant vos opérations de transport.
      </p>
      <Button onClick={onStartConversation} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-full text-base font-semibold shadow-lg" data-testid="welcome-start-btn">
        <MessageSquare className="w-5 h-5 mr-2" />
        Démarrer une conversation
      </Button>
    </div>
  </div>
);

// ============================================================
// Main Component
// ============================================================
const ClientMessagingPro = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [notification, setNotification] = useState(null);
  const [newConversationIds, setNewConversationIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // all | unread | open
  const [showDetails, setShowDetails] = useState(true);
  const [inThreadSearch, setInThreadSearch] = useState('');
  const [showInThreadSearch, setShowInThreadSearch] = useState(false);

  const messagesEndRef = useRef(null);
  const previousMessagesCount = useRef({});
  const audioRef = useRef(null);
  const selectedConversationRef = useRef(null);

  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND);
    audioRef.current.volume = 0.5;
  }, []);

  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [soundEnabled]);

  const fetchConversations = useCallback(async (silent = false) => {
    try {
      const response = await axios.get(`${API}/conversations`);
      const raw = response.data || [];
      const newConversations = raw.map(conv => ({
        ...conv,
        unreadCount: conv.unreadCountClient || 0,
      }));

      const currentSelectedId = selectedConversationRef.current?.id || selectedConversationRef.current?._id;
      newConversations.forEach(conv => {
        const convId = conv.id || conv._id;
        const prevCount = previousMessagesCount.current[convId] || 0;
        const currentCount = conv.unreadCount || 0;
        if (currentCount > prevCount && currentSelectedId !== convId) {
          playNotificationSound();
          setNotification({ content: conv.lastMessagePreview });
          setNewConversationIds(prev => new Set([...prev, convId]));
          if (Notification.permission === 'granted') {
            new Notification('Message du Support Baobab', { body: conv.lastMessagePreview, icon: '/favicon.ico' });
          }
        }
        previousMessagesCount.current[convId] = currentCount;
      });

      setConversations(prev => {
        if (prev.length !== newConversations.length) return newConversations;
        const sameSig = prev.every((c, i) => {
          const n = newConversations[i];
          return c.id === n.id
            && c.lastMessageAt === n.lastMessageAt
            && (c.unreadCount || 0) === (n.unreadCount || 0)
            && (c.lastMessagePreview || '') === (n.lastMessagePreview || '');
        });
        return sameSig ? prev : newConversations;
      });

      if (!silent) setLoading(false);
      if (!selectedConversationRef.current && newConversations.length > 0) {
        setSelectedConversation(newConversations[0]);
      }
    } catch (e) {
      console.error('Error fetching conversations:', e);
      if (!silent) setLoading(false);
    }
  }, [playNotificationSound]);

  const fetchMessages = useCallback(async (conversationId, silent = false) => {
    if (!conversationId) return;
    if (!silent) setLoadingMessages(true);
    try {
      const response = await axios.get(`${API}/messages?conversation_id=${conversationId}`);
      const newMsgs = response.data || [];
      setMessages(prev => {
        if (prev.length !== newMsgs.length) return newMsgs;
        const lp = prev[prev.length - 1];
        const ln = newMsgs[newMsgs.length - 1];
        if (!lp && !ln) return prev;
        if (!lp || !ln) return newMsgs;
        const sameSig = lp.id === ln.id
          && (lp.content || '') === (ln.content || '')
          && (lp.readBy?.length || 0) === (ln.readBy?.length || 0);
        return sameSig ? prev : newMsgs;
      });
    } catch (e) { console.error('Error fetching messages:', e); }
    finally { if (!silent) setLoadingMessages(false); }
  }, []);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    fetchConversations(false);
    const interval = setInterval(() => fetchConversations(true), 30000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  const selectedConvId = selectedConversation?.id || selectedConversation?._id || null;
  const { lastMessage: wsMsg, sendTyping } = useChatSocket(selectedConvId, user?.id, user?.role);
  const [typingUserName, setTypingUserName] = useState(null);
  const typingClearTimer = useRef(null);

  useEffect(() => {
    if (!wsMsg) return;
    if (wsMsg.type === 'new_message') {
      if (selectedConvId) fetchMessages(selectedConvId, true);
      setTypingUserName(null);
    } else if (wsMsg.type === 'conversation_updated') {
      fetchConversations(true);
    } else if (wsMsg.type === 'user_typing') {
      if (wsMsg.user_id === user?.id) return;
      if (wsMsg.is_typing) {
        const lastFromUser = [...messages].reverse().find(m => m.senderId === wsMsg.user_id);
        setTypingUserName(lastFromUser?.senderName || 'Support Baobab');
        if (typingClearTimer.current) clearTimeout(typingClearTimer.current);
        typingClearTimer.current = setTimeout(() => setTypingUserName(null), 4000);
      } else {
        setTypingUserName(null);
      }
    }
  }, [wsMsg, selectedConvId, fetchMessages, fetchConversations, user?.id, messages]);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
    if (selectedConversation) {
      const convId = selectedConversation.id || selectedConversation._id;
      fetchMessages(convId, false);
      setNewConversationIds(prev => {
        const next = new Set(prev);
        next.delete(convId);
        return next;
      });
      const interval = setInterval(() => fetchMessages(convId, true), 15000);
      return () => clearInterval(interval);
    }
  }, [selectedConversation, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (content, files = [], retryOf = null) => {
    if (!selectedConversation) return;
    const convId = selectedConversation.id || selectedConversation._id;
    // Optimistic insert : on affiche immédiatement le message en statut 'sending'
    const tmpId = retryOf?.id || `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const optimisticMsg = {
      id: tmpId,
      _tmp: true,
      conversationId: convId,
      senderId: user?.id,
      senderEmail: user?.email,
      senderName: user?.firstName || 'Vous',
      content,
      attachments: files.map(f => ({ filename: f.name, type: f.type })),
      status: 'sending',
      createdAt: new Date().toISOString(),
    };
    if (retryOf) {
      // Remplace le message en erreur par sa version 'sending'
      setMessages(prev => prev.map(m => (m.id === retryOf.id ? optimisticMsg : m)));
    } else {
      setMessages(prev => [...prev, optimisticMsg]);
    }
    try {
      const formData = new FormData();
      formData.append('content', content);
      formData.append('conversation_id', convId);
      if (selectedConversation.operationId) formData.append('operation_id', selectedConversation.operationId);
      files.forEach(file => formData.append('files', file));
      await axios.post(`${API}/messages`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      // Refetch (le tmp sera remplacé par le vrai message backend)
      fetchMessages(convId, true);
      fetchConversations(true);
    } catch (e) {
      // Marque le message optimistic en erreur — déclenche le bouton Réessayer
      setMessages(prev => prev.map(m => (m.id === tmpId ? { ...m, status: 'error', error: true, _retryPayload: { content, files } } : m)));
      toast({ title: 'Erreur', description: "Impossible d'envoyer le message", variant: 'destructive' });
    }
  };

  const handleRetry = (msg) => {
    if (!msg) return;
    const payload = msg._retryPayload || { content: msg.content || '', files: [] };
    handleSendMessage(payload.content, payload.files || [], msg);
  };

  const handleStartConversation = async () => {
    try {
      const response = await axios.post(`${API}/conversations`, {
        type: 'DIRECT', clientId: user?.id, subject: 'Nouvelle conversation',
      });
      setSelectedConversation(response.data);
      fetchConversations(true);
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible de démarrer la conversation', variant: 'destructive' });
    }
  };

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
  const unreadCount = conversations.filter(c => (c.unreadCount || 0) > 0).length;
  const openCount = conversations.filter(c => c.status !== 'RESOLVED').length;

  const filteredConversations = useMemo(() => {
    let list = conversations;
    if (activeTab === 'unread') list = list.filter(c => (c.unreadCount || 0) > 0);
    else if (activeTab === 'open') list = list.filter(c => c.status !== 'RESOLVED');
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(c =>
        (c.lastMessagePreview || '').toLowerCase().includes(q) ||
        (c.operationReference || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [conversations, activeTab, searchQuery]);

  // Group messages by date
  const groupMessagesByDate = (msgs) => {
    const groups = {};
    msgs.forEach(msg => {
      const d = new Date(msg.createdAt || msg.timestamp);
      const today = new Date(); today.setHours(0,0,0,0);
      const msgDay = new Date(d); msgDay.setHours(0,0,0,0);
      const diffDays = Math.floor((today - msgDay) / 86400000);
      let label;
      if (diffDays === 0) label = "Aujourd'hui";
      else if (diffDays === 1) label = 'Hier';
      else label = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
      if (!groups[label]) groups[label] = [];
      groups[label].push(msg);
    });
    return groups;
  };

  const messageGroups = useMemo(() => {
    const filtered = inThreadSearch.trim()
      ? messages.filter(m => (m.content || '').toLowerCase().includes(inThreadSearch.toLowerCase()))
      : messages;
    return groupMessagesByDate(filtered);
  }, [messages, inThreadSearch]);

  // ID du premier message non lu (inséré avant lui le séparateur "Nouveaux messages")
  // On capture le compteur d'unread au moment où la conversation s'ouvre, pour ne pas faire
  // disparaître le séparateur au premier markRead asynchrone.
  const initialUnreadRef = useRef({});
  useEffect(() => {
    if (!selectedConvId) return;
    if (initialUnreadRef.current[selectedConvId] === undefined) {
      const conv = conversations.find(c => (c.id || c._id) === selectedConvId);
      initialUnreadRef.current[selectedConvId] = conv?.unreadCount || conv?.unreadCountClient || 0;
    }
  }, [selectedConvId, conversations]);

  const firstUnreadMsgId = useMemo(() => {
    if (!selectedConvId) return null;
    const initialUnread = initialUnreadRef.current[selectedConvId] || 0;
    if (initialUnread === 0) return null;
    // Messages reçus de l'agent (pas isOwn), pris dans l'ordre chronologique
    const incoming = messages.filter(m => m.senderId !== user?.id && m.senderEmail !== user?.email);
    if (incoming.length === 0) return null;
    const firstUnreadIdx = Math.max(0, incoming.length - initialUnread);
    return incoming[firstUnreadIdx]?.id || incoming[firstUnreadIdx]?._id || null;
  }, [messages, selectedConvId, user?.id, user?.email]);

  return (
    <div className="h-[calc(100vh-110px)] flex flex-col bg-white rounded-xl overflow-hidden shadow-sm border border-zinc-200" data-testid="client-messaging-pro">
      {notification && <NotificationAlert message={notification} onDismiss={() => setNotification(null)} />}

      <div className="flex-1 flex overflow-hidden">
        {/* ================================================================ */}
        {/* LEFT — Conversations list                                          */}
        {/* ================================================================ */}
        {conversations.length > 0 && (
          <div className={`w-full lg:w-[340px] bg-white border-r border-zinc-100 flex flex-col ${selectedConversation ? 'hidden lg:flex' : 'flex'}`}>
            {/* Header vert */}
            <div className="px-5 py-4 bg-emerald-600 text-white flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
                  <MessageSquare className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="font-bold text-base flex items-center gap-2">
                    Messagerie
                    {totalUnread > 0 && (
                      <span className="bg-white text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{totalUnread}</span>
                    )}
                  </h2>
                  <p className="text-xs text-white/80 mt-0.5">Vos conversations avec le support</p>
                </div>
              </div>
              <button
                onClick={handleStartConversation}
                className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 backdrop-blur flex items-center justify-center transition-colors shrink-0"
                title="Nouvelle conversation"
                data-testid="new-conversation-btn"
              >
                <Edit3 className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Search + Filter */}
            <div className="px-4 py-3 border-b border-zinc-100">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
                  <Input
                    placeholder="Rechercher une conversation..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-9 text-sm bg-zinc-50 border-zinc-100 focus:bg-white"
                    data-testid="conv-search-input"
                  />
                </div>
                <button className="w-9 h-9 rounded-lg border border-zinc-200 flex items-center justify-center text-zinc-500 hover:bg-zinc-50 transition-colors" title="Filtres">
                  <SlidersHorizontal className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="px-4 py-2 border-b border-zinc-100 flex items-center gap-1.5">
              {[
                { id: 'all', label: 'Tous', count: conversations.length },
                { id: 'unread', label: 'Non lus', count: unreadCount },
                { id: 'open', label: 'Ouverts', count: openCount },
              ].map(t => {
                const active = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                      active ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'text-zinc-500 hover:bg-zinc-100'
                    }`}
                    data-testid={`tab-${t.id}`}
                  >
                    {t.label}
                    {t.count > 0 && (
                      <span className={`text-[10px] font-bold px-1.5 rounded-full ${active ? 'bg-emerald-600 text-white' : 'bg-zinc-200 text-zinc-600'}`}>
                        {t.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* List */}
            <ScrollArea className="flex-1">
              {filteredConversations.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <p className="text-sm text-zinc-400">Aucune conversation</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-50">
                  {filteredConversations.map(conv => {
                    const convKey = conv.id || conv._id;
                    const selKey = selectedConversation?.id || selectedConversation?._id;
                    const isSel = !!convKey && convKey === selKey;
                    return (
                      <ConversationItem
                        key={convKey}
                        conversation={conv}
                        isSelected={isSel}
                        onClick={() => setSelectedConversation(conv)}
                      />
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        )}

        {/* ================================================================ */}
        {/* CENTER — Chat thread                                              */}
        {/* ================================================================ */}
        <div className={`flex-1 flex flex-col min-w-0 ${selectedConversation ? 'flex' : conversations.length > 0 ? 'hidden lg:flex' : 'flex'}`}>
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full" />
            </div>
          ) : conversations.length === 0 ? (
            <WelcomeScreen onStartConversation={handleStartConversation} />
          ) : selectedConversation ? (
            <>
              <ChatHeader
                conversation={selectedConversation}
                onBack={() => setSelectedConversation(null)}
                soundEnabled={soundEnabled}
                onToggleSound={() => setSoundEnabled(!soundEnabled)}
                onToggleSearch={() => setShowInThreadSearch(v => !v)}
                onToggleDetails={() => setShowDetails(v => !v)}
              />

              {showInThreadSearch && (
                <div className="px-5 py-2 bg-amber-50/40 border-b border-amber-100">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
                    <Input
                      autoFocus
                      placeholder="Rechercher dans la conversation..."
                      value={inThreadSearch}
                      onChange={(e) => setInThreadSearch(e.target.value)}
                      className="pl-9 pr-8 h-8 text-sm bg-white"
                      data-testid="in-thread-search"
                    />
                    {inThreadSearch && (
                      <button onClick={() => { setInThreadSearch(''); setShowInThreadSearch(false); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              <ScrollArea className="flex-1 px-5 py-3 bg-gradient-to-b from-zinc-50/30 to-white">
                {loadingMessages ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center text-zinc-500 bg-white p-6 rounded-xl border border-zinc-100">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 text-emerald-600" />
                      <p className="font-medium">Commencez la conversation</p>
                      <p className="text-sm text-zinc-400">Envoyez un message à notre équipe</p>
                    </div>
                  </div>
                ) : (
                  <div className="py-2">
                    {Object.entries(messageGroups).map(([date, msgs]) => (
                      <div key={date}>
                        <div className="flex items-center justify-center my-4">
                          <span className="px-3 py-1 bg-white border border-zinc-100 rounded-full text-[11px] font-medium text-zinc-500 shadow-sm">
                            {date}
                          </span>
                        </div>
                        {msgs.map((msg, idx) => {
                          const isOwn = msg.senderId === user?.id || msg.senderEmail === user?.email;
                          const lastAgent = [...messages].reverse().find(m => m.senderId !== user?.id && m.senderEmail !== user?.email);
                          const isLatestFromAgent = lastAgent && (msg.id === lastAgent.id || msg._id === lastAgent._id);
                          const msgId = msg.id || msg._id;
                          const showNewSeparator = firstUnreadMsgId && msgId === firstUnreadMsgId;
                          return (
                            <React.Fragment key={msgId || idx}>
                              {showNewSeparator && (
                                <div className="flex items-center gap-3 my-4 px-1" data-testid="new-messages-separator" role="separator" aria-label="Nouveaux messages">
                                  <div className="flex-1 h-px bg-emerald-200" />
                                  <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-[11px] font-bold uppercase tracking-wider rounded-full border border-emerald-200 shadow-sm">
                                    Nouveaux messages
                                  </span>
                                  <div className="flex-1 h-px bg-emerald-200" />
                                </div>
                              )}
                              <MessageBubble message={msg} isOwn={isOwn} isLatestFromAgent={isLatestFromAgent} onRetry={handleRetry} />
                            </React.Fragment>
                          );
                        })}
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              <MessageInput onSend={handleSendMessage} onTyping={sendTyping} typingUserName={typingUserName} />
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-zinc-50">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 text-zinc-200 mx-auto mb-4" />
                <p className="text-zinc-400">Sélectionnez une conversation</p>
              </div>
            </div>
          )}
        </div>

        {/* ================================================================ */}
        {/* RIGHT — Conversation details                                      */}
        {/* ================================================================ */}
        {selectedConversation && showDetails && (
          <ConversationDetails conversation={selectedConversation} onClose={() => setShowDetails(false)} />
        )}
      </div>
    </div>
  );
};

export default ClientMessagingPro;
